/** @jsxImportSource @emotion/react */
import { useRef, useState } from 'react';

import type { ScanTaskOptimized } from '@kaiizen/kai-scan-template-system';

import { type ProjectStateLifecycle, useProjectStateLifecycle } from '@/Client/Layout/useProjectStateLifecycle';
import { isTaskScopedSafe } from '@/Client/Lib/scopeNavigatorPartPresentersLib';
import { taskParticipatesInReview } from '@/Client/Partials/FocusedScopeEditor/focusedGroupOverviewLib';
import {
  useAuth,
  useCreateReviewNotes,
  useCreateUnapprovalNote,
  useFeatureFlags,
  useScanEditingContextSlice,
} from '@/Client/State';
import {
  APPROVE_BUTTON_STYLE,
  APPROVE_CONTROL_STYLE,
  APPROVED_CHIP_STYLE,
  KaiNavTaskApprovalCheckIcon,
  SUBMITTING_STYLE,
  WRAPPER_STYLE,
} from './KaiNavTaskApprovalStyles';
import { canApproveTurnBasedDraftTask, turnBasedDraftOffersTaskApproval } from './turnBasedTaskApprovalLib';

// Single source of truth so a copy change only needs to happen here.
export const TASK_APPROVE_LABEL = 'Approve';
export const TASK_APPROVED_LABEL = 'Approved';
export const NO_APPROVE_PERMISSION_TITLE = 'You do not have permission to approve scope';

/**
 * Whether `KaiNavTaskApprovalWidget` would render anything for this task —
 * exported so a host that needs to know in advance (the row's own kebab,
 * which stays visible whenever the approve control sits beside it) doesn't
 * have to re-derive this by hand.
 */
export const taskOffersApproval = (
  task: ScanTaskOptimized,
  {
    projectStateLifecycle,
    scanIsChangeOrder,
    changedScopedTaskIds,
    turnBasedHandoff = false,
    allowTurnBasedDraftApproval = false,
  }: {
    projectStateLifecycle: ProjectStateLifecycle;
    scanIsChangeOrder: boolean;
    changedScopedTaskIds: readonly string[];
    turnBasedHandoff?: boolean;
    allowTurnBasedDraftApproval?: boolean;
  },
): boolean => {
  const stageOffersApproval = turnBasedHandoff
    ? turnBasedDraftOffersTaskApproval({ projectStateLifecycle, scanIsChangeOrder, allowTurnBasedDraftApproval })
    : projectStateLifecycle === 'review' || projectStateLifecycle === 'closeout';
  if (!stageOffersApproval || isTaskScopedSafe(task) !== true) return false;
  return taskParticipatesInReview(task.id, scanIsChangeOrder, changedScopedTaskIds);
};

/**
 * Approve one task, from the row that names it.
 *
 * Takes only the task. Which stage the project is in, whether this task is
 * already approved, and how an approval is filed are all read here rather than
 * handed down — the row stays a row, and the navigator's hosts learn nothing
 * about approval.
 *
 * In a turn-based draft, the designated Project Manager sees the per-task
 * review control without taking the work turn. The task approvals still come
 * before the separate final approval action. Outside that workflow, the
 * established review and closeout behavior is unchanged.
 */
export const KaiNavTaskApprovalWidget = ({ task }: { task: ScanTaskOptimized }) => {
  const projectStateLifecycle = useProjectStateLifecycle();
  const { approvalStatusByTaskId, scanIsChangeOrder, changedScopedTaskIds, project } = useScanEditingContextSlice([
    'approvalStatusByTaskId',
    'scanIsChangeOrder',
    'changedScopedTaskIds',
    'project',
  ]);
  const { userCan, userId } = useAuth();
  const { featureFlags } = useFeatureFlags();
  const createReviewNotes = useCreateReviewNotes();
  const createUnapprovalNote = useCreateUnapprovalNote();
  const [submitting, setSubmitting] = useState(false);
  const wrapperRef = useRef<HTMLSpanElement>(null);

  const turnBasedProject = featureFlags.turnBasedHandoff && !!project;
  const canApproveTurnBasedDraft = canApproveTurnBasedDraftTask({
    turnBasedHandoff: turnBasedProject,
    managerIds: project?.managerIds,
    userId,
  });

  if (
    !taskOffersApproval(task, {
      projectStateLifecycle,
      scanIsChangeOrder,
      changedScopedTaskIds,
      turnBasedHandoff: turnBasedProject,
      allowTurnBasedDraftApproval: canApproveTurnBasedDraft,
    })
  ) return null;

  const approved = approvalStatusByTaskId[task.id] === 'approved';
  // Turn-based task approval is deliberately narrower than generic
  // organization approval: only the designated Project Manager can approve a
  // task. The legacy review flow retains its existing gate.
  const canApprove = turnBasedProject ? canApproveTurnBasedDraft : userCan('approve', 'Scan');

  return (
    <span css={WRAPPER_STYLE} ref={wrapperRef}>
      <button
        type='button'
        css={[APPROVE_CONTROL_STYLE, APPROVE_BUTTON_STYLE, approved ? APPROVED_CHIP_STYLE : '', submitting && SUBMITTING_STYLE]}
        disabled={submitting || !canApprove}
        title={canApprove ? undefined : NO_APPROVE_PERMISSION_TITLE}
        onClick={async event => {
          event.stopPropagation();
          setSubmitting(true);
          try {
            if (approved) {
              await createUnapprovalNote([task.id]);
            } else {
              await createReviewNotes([task.id], 'approval');
            }
          } finally {
            setSubmitting(false);
          }
        }}
      >
        {approved ? <KaiNavTaskApprovalCheckIcon /> : null}
        {approved ? TASK_APPROVED_LABEL : TASK_APPROVE_LABEL}
      </button>
    </span>
  );
};
